import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import { CHART_COLORS, CHART_FONT_SIZES } from './chartTheme';

const BarChart = ({ dataPoints = [], width = 500, height = 450 }) => {
  const chartRef = useRef();

  useEffect(() => {
    if (dataPoints.length === 0) return;

    const drawChart = () => {
      const shouldRotate = dataPoints.length > 7;
      const margin = { top: 20, right: 30, bottom: shouldRotate ? 110 : 80, left: 50 };

      d3.select(chartRef.current).selectAll('*').remove();

      const svg = d3.select(chartRef.current)
        .attr('width', width + margin.left + margin.right)
        .attr('height', height + margin.top + margin.bottom)
        .append('g')
        .attr('transform', `translate(${margin.left}, ${margin.top})`);

      const xScale = d3.scaleBand()
        .domain(dataPoints.map(d => d.label))
        .range([0, width])
        .padding(0.1);

      const yScale = d3.scaleLinear()
        .domain([0, d3.max(dataPoints, d => d.value) + 20])
        .range([height, 0]);

      const grid = svg.append('g')
        .attr('class', 'grid')
        .call(d3.axisLeft(yScale)
          .tickSize(-width)
          .tickFormat(d => d)
        );

      grid.selectAll('line')
        .attr('stroke', CHART_COLORS.grid)
        .attr('stroke-width', 1)
        .attr('shape-rendering', 'crispEdges');

      grid.selectAll('text').attr('fill', CHART_COLORS.label);

      svg.select('.grid .domain').remove();

      svg.selectAll('.bar')
        .data(dataPoints)
        .enter()
        .append('rect')
        .attr('x', d => xScale(d.label) + xScale.bandwidth() * 0.25)
        .attr('y', d => yScale(d.value))
        .attr('width', xScale.bandwidth() * 0.3)
        .attr('height', d => height - yScale(d.value))
        .attr('fill', CHART_COLORS.bar)
        .attr('rx', 2)
        .attr('ry', 2);

      svg.selectAll('.text')
        .data(dataPoints)
        .enter()
        .append('text')
        .attr('x', d => xScale(d.label) + xScale.bandwidth() / 2)
        .attr('y', d => (d.value > 0 ? yScale(d.value) - 5 : yScale(d.value) + 15))
        .attr('fill', CHART_COLORS.label)
        .attr('font-size', CHART_FONT_SIZES.label)
        .attr('font-weight', 'normal')
        .attr('text-anchor', 'middle')
        .text(d => (d.value > 0 ? d.value : ''));

      const xAxis = svg.append('g')
        .attr('transform', `translate(0,${height})`)
        .call(d3.axisBottom(xScale).tickSize(0).tickPadding(15));

      xAxis.select('.domain').remove();

      xAxis.selectAll('text')
        .style('text-anchor', shouldRotate ? 'end' : 'middle')
        .attr('dy', shouldRotate ? '0.35em' : '10px')
        .attr('dx', shouldRotate ? '-0.5em' : '0')
        .attr('transform', shouldRotate ? 'rotate(-40)' : null)
        .attr('font-size', CHART_FONT_SIZES.axis)
        .attr('fill', CHART_COLORS.label)
        .style('opacity', (d) => {
          const dataPoint = dataPoints.find(point => point.label === d);
          return dataPoint?.showLabel === false ? 0 : 1;
        });

      svg.append('g')
        .call(d3.axisLeft(yScale).tickSize(0))
        .select('.domain').remove();
    };

    drawChart();
  }, [dataPoints, width, height]);

  return <svg ref={chartRef}></svg>;
};

export default BarChart;
